import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	Box,
	Button,
	CircularProgress,
	InputAdornment,
	Tab,
	Tabs,
	TextField,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import { getEscapeRooms, postEscapeRoom } from "../../../../api/escape-room";
import { getDefaultRegistrationPage } from "./defaultRegistrationPage";
import { REGISTRATION_PAGE_ID } from "./escapeRoomEditorConstants";
import { PagePreviewThumbnail, THUMB_WIDTH, THUMB_HEIGHT } from "./escapeRoomPagePreviewThumbnail";
import { resolveEscapeRoomMediaUrl } from "./escapeRoomMediaUrl";
import { alertError, alertSuccess } from "../../../../utils/logger";

const PREVIEW_W = THUMB_WIDTH;
const PREVIEW_H = THUMB_HEIGHT;

function ownerLabel(er) {
	const owner = er?.createdBy;
	if (!owner || typeof owner !== "object") return "—";
	const name = String(owner.fullName || "").trim();
	const email = String(owner.email || "").trim();
	if (name && email) return `${name} · ${email}`;
	return name || email || "—";
}

function formatDate(value) {
	if (!value) return "—";
	try {
		return new Date(value).toLocaleDateString("es-ES", {
			day: "2-digit",
			month: "short",
			year: "numeric",
		});
	} catch {
		return "—";
	}
}

function pickPreviewPage(escapeRoom) {
	const pages = Array.isArray(escapeRoom?.pages) ? [...escapeRoom.pages] : [];
	if (!pages.length) return null;
	pages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	const playable = pages.find((p) => (p.pageId || p._id) !== REGISTRATION_PAGE_ID);
	return playable || pages[0];
}

function EscapeRoomPreviewThumb({ escapeRoom }) {
	const page = pickPreviewPage(escapeRoom);
	const placeholderStyle = {
		width: PREVIEW_W,
		height: PREVIEW_H,
		borderRadius: 8,
		background: "#f1f5f9",
		border: "1px solid #e2e8f0",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "#94a3b8",
		fontSize: 11,
		fontWeight: 700,
		flexShrink: 0,
	};

	if (!page) {
		return <Box style={placeholderStyle}>Sin páginas</Box>;
	}

	const pageId = page.pageId || page._id;
	const isRegistration = pageId === REGISTRATION_PAGE_ID;
	const bgUrl = !isRegistration && page.backgroundImageUrl
		? resolveEscapeRoomMediaUrl(page.backgroundImageUrl)
		: undefined;

	return (
		<Box
			style={{
				width: PREVIEW_W,
				height: PREVIEW_H,
				borderRadius: 8,
				overflow: "hidden",
				flexShrink: 0,
				border: "1px solid #e2e8f0",
				boxShadow: "0 4px 14px rgba(15,23,42,0.08)",
				background: "#fff",
			}}
		>
			<PagePreviewThumbnail
				elements={page.elements || []}
				registrationDesign={isRegistration ? page.registrationDesign : undefined}
				isRegistration={isRegistration}
				backgroundColor={!isRegistration ? page.backgroundColor : undefined}
				backgroundImageUrl={bgUrl}
				backgroundImageOpacity={!isRegistration ? page.backgroundImageOpacity : undefined}
			/>
		</Box>
	);
}

function EscapeRoomSearchPanel({ onSelect }) {
	const [nameQuery, setNameQuery] = useState("");
	const [userQuery, setUserQuery] = useState("");
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searched, setSearched] = useState(false);

	const runSearch = useCallback((opts = {}) => {
		setLoading(true);
		setSearched(true);
		return getEscapeRooms({
			name: opts.name ?? nameQuery,
			user: opts.user ?? userQuery,
			limit: 80,
		})
			.then((res) => {
				setItems(Array.isArray(res.data) ? res.data : []);
			})
			.catch((err) => {
				setItems([]);
				alertError({ error: err, customMessage: "No se pudo buscar escape rooms." });
			})
			.finally(() => setLoading(false));
	}, [nameQuery, userQuery]);

	useEffect(() => {
		runSearch({ name: "", user: "" });
	}, [runSearch]);

	const emptyMessage = useMemo(() => {
		if (loading) return null;
		if (!searched) return "Escribe un nombre o un usuario y pulsa Buscar.";
		if (nameQuery.trim() || userQuery.trim()) {
			return "No hay escape rooms que coincidan con la búsqueda.";
		}
		return "No hay escape rooms todavía.";
	}, [loading, searched, nameQuery, userQuery]);

	return (
		<Box>
			<Box fontSize={14} color="#64748b" mb={2} lineHeight={1.55}>
				Busca entre todos los escape rooms de los usuarios. Al seleccionar uno podrás revisarlo en el editor y pulsar{" "}
				<strong>Publicar como plantilla</strong>.
			</Box>

			<Box display="flex" flexWrap="wrap" style={{ gap: 12, marginBottom: 16 }}>
				<TextField
					label="Nombre del escape room"
					variant="outlined"
					size="small"
					value={nameQuery}
					onChange={(e) => setNameQuery(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							runSearch();
						}
					}}
					style={{ minWidth: 240, flex: "1 1 240px" }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon style={{ color: "#94a3b8", fontSize: 20 }} />
							</InputAdornment>
						),
					}}
				/>
				<TextField
					label="Usuario (email o nombre)"
					variant="outlined"
					size="small"
					value={userQuery}
					onChange={(e) => setUserQuery(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							runSearch();
						}
					}}
					style={{ minWidth: 240, flex: "1 1 240px" }}
				/>
				<Button
					variant="contained"
					color="primary"
					onClick={() => runSearch()}
					disabled={loading}
					style={{ alignSelf: "center", minHeight: 40 }}
				>
					{loading ? "Buscando…" : "Buscar"}
				</Button>
			</Box>

			{loading ? (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress size={32} />
				</Box>
			) : items.length ? (
				<Box display="flex" flexDirection="column" style={{ gap: 12 }}>
					{items.map((er) => {
						const pageCount = Array.isArray(er.pages) ? er.pages.length : 0;
						const published = Boolean(er.templatePublished);
						const previewPage = pickPreviewPage(er);
						const previewLabel = previewPage
							? ((previewPage.pageId || previewPage._id) === REGISTRATION_PAGE_ID
								? "Vista: Registro"
								: `Vista: ${previewPage.name || "Página"}`)
							: null;

						return (
							<Box
								key={er._id}
								display="flex"
								alignItems="stretch"
								style={{
									gap: 16,
									padding: 14,
									borderRadius: 14,
									border: "1px solid #e2e8f0",
									background: "#fff",
									boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
								}}
							>
								<Box style={{ width: PREVIEW_W, flexShrink: 0 }}>
									<EscapeRoomPreviewThumb escapeRoom={er} />
									{previewLabel ? (
										<Box fontSize={10} fontWeight={700} color="#94a3b8" mt={0.75} textAlign="center">
											{previewLabel}
										</Box>
									) : null}
								</Box>

								<Box flex={1} minWidth={0} display="flex" flexDirection="column" justifyContent="center">
									<Box fontWeight={800} fontSize={16} color="#0f172a" style={{ wordBreak: "break-word" }}>
										{er.name || "Sin nombre"}
									</Box>
									<Box fontSize={13} color="#475569" mt={0.5} style={{ wordBreak: "break-word" }}>
										{ownerLabel(er)}
									</Box>
									<Box display="flex" flexWrap="wrap" alignItems="center" mt={1.25} style={{ gap: 8 }}>
										<span style={{ fontSize: 12, fontWeight: 700, color: "#64748b" }}>
											{pageCount} página{pageCount === 1 ? "" : "s"}
										</span>
										<span style={{ fontSize: 12, color: "#cbd5e1" }}>·</span>
										<span style={{ fontSize: 12, color: "#94a3b8" }}>
											Actualizado {formatDate(er.updatedAt || er.createdAt)}
										</span>
										<span
											style={{
												display: "inline-flex",
												padding: "3px 10px",
												borderRadius: 999,
												fontSize: 11,
												fontWeight: 800,
												background: published ? "#dcfce7" : "#f1f5f9",
												color: published ? "#15803d" : "#64748b",
											}}
										>
											{published ? "Publicada" : "Privada"}
										</span>
									</Box>
								</Box>

								<Box display="flex" alignItems="center" flexShrink={0}>
									<Button
										variant="contained"
										color="primary"
										onClick={() => onSelect(er._id)}
										style={{ textTransform: "none", fontWeight: 700, borderRadius: 10, minWidth: 120 }}
									>
										Seleccionar
									</Button>
								</Box>
							</Box>
						);
					})}
				</Box>
			) : (
				<Box
					py={4}
					px={2}
					textAlign="center"
					style={{ color: "#64748b", background: "#f8fafc", borderRadius: 12, border: "1px dashed #cbd5e1" }}
				>
					{emptyMessage}
				</Box>
			)}
		</Box>
	);
}

export default function EscapeRoomCreatePage() {
	const [tab, setTab] = useState(0);
	const [name, setName] = useState("");
	const [loading, setLoading] = useState(false);
	const history = useHistory();

	const handleSubmit = (e) => {
		if (e) e.preventDefault();
		if (!name.trim() || loading) return;
		setLoading(true);
		postEscapeRoom({
			name: name.trim(),
			pages: [getDefaultRegistrationPage()],
		})
			.then((res) => {
				if (res.status === 201 && res.data?._id) {
					alertSuccess({ customMessage: "Escape room creado." });
					history.push(`/escape-rooms/${res.data._id}`);
				}
			})
			.catch((err) => {
				alertError({ error: err, customMessage: "No se pudo crear el escape room." });
				setLoading(false);
			});
	};

	const handleSelectExisting = (id) => {
		if (!id) return;
		history.push(`/escape-rooms/${id}`, { fromTemplatePicker: true });
	};

	return (
		<Card>
			<CardHeader title="Nueva plantilla" />
			<CardBody>
				<Tabs
					value={tab}
					onChange={(_, next) => setTab(next)}
					indicatorColor="primary"
					textColor="primary"
					style={{ marginBottom: 20, borderBottom: "1px solid #e2e8f0" }}
				>
					<Tab icon={<LibraryBooksIcon />} label="Desde escape room existente" style={{ textTransform: "none", fontWeight: 700, minHeight: 56 }} />
					<Tab icon={<AddIcon />} label="Crear desde cero" style={{ textTransform: "none", fontWeight: 700, minHeight: 56 }} />
				</Tabs>

				{tab === 0 ? (
					<EscapeRoomSearchPanel onSelect={handleSelectExisting} />
				) : (
					<Box maxWidth={520}>
						<Box fontSize={14} color="#64748b" mb={2} lineHeight={1.55}>
							Crea una plantilla vacía con la página de registro por defecto. Después podrás diseñarla y publicarla en el catálogo.
						</Box>
						<form onSubmit={handleSubmit}>
							<TextField
								autoFocus
								fullWidth
								label="Nombre de la plantilla"
								value={name}
								onChange={(e) => setName(e.target.value)}
								margin="normal"
								variant="outlined"
								disabled={loading}
							/>
							<div style={{ marginTop: 16 }}>
								<Button
									type="submit"
									variant="contained"
									color="primary"
									disabled={!name.trim() || loading}
								>
									{loading ? "Creando…" : "Crear y abrir editor"}
								</Button>
							</div>
						</form>
					</Box>
				)}
			</CardBody>
		</Card>
	);
}
